import React, { useState, useEffect, useCallback } from 'react';
import { questionsAPI, getErrorMessage } from '../services/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import type { Question, CreateQuestionRequest } from '../types';

interface QuestionsTabProps {
  jobId: number;
}

function QuestionsTab({ jobId }: QuestionsTabProps) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [formData, setFormData] = useState<CreateQuestionRequest>({
    question_text: '',
    duration: 60,
    order: 0,
  });

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await questionsAPI.getAll(jobId);
      setQuestions(data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleCreate = () => {
    setEditingQuestion(null);
    setFormData({
      question_text: '',
      duration: 60,
      order: questions.length,
    });
    setShowModal(true);
  };

  const handleEdit = (question: Question) => {
    setEditingQuestion(question);
    setFormData({
      question_text: question.question_text,
      duration: question.duration,
      order: question.order,
    });
    setShowModal(true);
  };

  const handleDelete = async (questionId: number) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await questionsAPI.delete(jobId, questionId);
      loadQuestions();
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to delete question');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingQuestion) {
        await questionsAPI.update(jobId, editingQuestion.id, formData);
      } else {
        await questionsAPI.create(jobId, formData);
      }
      setShowModal(false);
      loadQuestions();
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to save question');
    }
  };

  if (loading) {
    return <div className="text-center py-10 text-gray-600">Loading questions...</div>;
  }

  if (error) {
    return <div className="bg-danger-light text-danger-text px-4 py-3 rounded-md">{error}</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <Card className="p-5">
          <h4 className="text-sm uppercase tracking-wider text-gray-600 mb-2">Total Questions</h4>
          <p className="text-[32px] font-bold text-dark m-0">{questions.length}</p>
        </Card>
        <Button onClick={handleCreate}>
          + Add Question
        </Button>
      </div>

      {questions.length === 0 ? (
        <div className="text-center py-16">
          <h3 className="text-lg font-medium text-gray-700 mb-2">No questions found</h3>
          <p className="text-gray-500 mb-5">Add interview questions for this job</p>
          <Button onClick={handleCreate}>
            + Add Question
          </Button>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Order</TableHead>
                <TableHead>Question</TableHead>
                <TableHead className="w-32">Duration</TableHead>
                <TableHead className="w-52">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {questions
                .sort((a, b) => a.order - b.order)
                .map(question => (
                  <TableRow key={question.id}>
                    <TableCell>{question.order + 1}</TableCell>
                    <TableCell>{question.question_text}</TableCell>
                    <TableCell>{question.duration}s</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" onClick={() => handleEdit(question)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => handleDelete(question.id)}>
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add New Question'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="question_text">Question Text *</Label>
                <Textarea
                  id="question_text"
                  required
                  value={formData.question_text}
                  onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                  placeholder="Enter the interview question..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration">Duration (seconds) *</Label>
                <Input
                  id="duration"
                  type="number"
                  required
                  min="10"
                  max="600"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
                />
                <p className="text-xs text-gray-600">How long the candidate has to answer (10-600 seconds)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="order">Order *</Label>
                <Input
                  id="order"
                  type="number"
                  required
                  min="0"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                />
                <p className="text-xs text-gray-600">Question display order (0-based)</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingQuestion ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default QuestionsTab;
